import { useState } from "react";
import type { PurchaseOrderDto, SupplierDto } from "@grizzly/shared";
import { purchaseOrderStatus } from "@grizzly/shared";
import { formatAmount } from "../lib/format.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, PageHeader } from "../components/ui/card.js";
import { DataTable, type Column } from "../components/ui/dataTable.js";
import { SupplierDialog } from "../components/purchasing/supplierDialog.js";
import { PurchaseOrderDialog } from "../components/purchasing/purchaseOrderDialog.js";
import { PurchaseOrderPanel } from "../components/purchasing/purchaseOrderPanel.js";
import { usePurchasingData } from "../lib/usePurchasingData.js";
import { useI18n, type MessageKey } from "../i18n/index.js";

export function PurchasingPage() {
  const { t } = useI18n();
  const { suppliers, orders, jobs, materials, locations, error, reload } =
    usePurchasingData();
  const [selected, setSelected] = useState<string | null>(null);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);

  const supplierColumns: Column<SupplierDto>[] = [
    { key: "code", header: t("common.fields.code"), render: (r) => r.code },
    {
      key: "name",
      header: t("common.fields.name"),
      render: (r) => <span className="font-medium text-ink">{r.name}</span>,
    },
    {
      key: "contact",
      header: t("purchasing.contact"),
      render: (r) => r.contactName ?? "-",
    },
    { key: "phone", header: t("common.fields.phone"), render: (r) => r.phone ?? "-" },
  ];

  const orderColumns: Column<PurchaseOrderDto>[] = [
    {
      key: "number",
      header: t("purchasing.order"),
      render: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r.id)}
          className="tabular font-medium text-action-primary-text hover:underline"
        >
          {r.number}
        </button>
      ),
    },
    { key: "supplier", header: t("purchasing.supplier"), render: (r) => r.supplierName },
    {
      key: "status",
      header: t("common.fields.status"),
      render: (r) => (
        <Badge tone={purchaseOrderStatus(r.status).tone}>
          {t(`status.purchaseOrder.${r.status}` as MessageKey)}
        </Badge>
      ),
    },
    { key: "job", header: t("purchasing.job"), render: (r) => r.jobNumber ?? "-" },
    {
      key: "total",
      header: t("common.fields.total"),
      render: (r) => <span className="tabular">{formatAmount(r.totalMinor)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title={t("nav.purchasing")}
        description={t("purchasing.description")}
        actions={
          <>
            <Button variant="secondary" onClick={() => setSupplierOpen(true)}>
              {t("purchasing.addSupplier")}
            </Button>
            <Button variant="primary" onClick={() => setPoOpen(true)}>
              {t("purchasing.newPurchaseOrder")}
            </Button>
          </>
        }
      />

      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[1fr_1.2fr]">
        <div className="space-y-4">
          <section>
            <h2 className="mb-2 text-sm font-semibold text-ink">
              {t("purchasing.suppliers")}
            </h2>
            <DataTable
              caption={t("purchasing.suppliers")}
              rows={suppliers}
              rowKey={(r) => r.id}
              columns={supplierColumns}
            />
          </section>
          <section>
            <h2 className="mb-2 text-sm font-semibold text-ink">
              {t("purchasing.purchaseOrders")}
            </h2>
            <DataTable
              caption={t("purchasing.purchaseOrders")}
              rows={orders}
              rowKey={(r) => r.id}
              columns={orderColumns}
              emptyTitle={t("purchasing.emptyTitle")}
              emptyDescription={t("purchasing.emptyDescription")}
            />
          </section>
        </div>

        {selected ? (
          <Card>
            <PurchaseOrderPanel
              orderId={selected}
              locations={locations}
              onChanged={reload}
            />
          </Card>
        ) : (
          <Card>
            <p className="text-sm text-ink-muted">{t("purchasing.selectOrder")}</p>
          </Card>
        )}
      </div>

      <SupplierDialog
        open={supplierOpen}
        onOpenChange={setSupplierOpen}
        onCreated={reload}
      />
      <PurchaseOrderDialog
        open={poOpen}
        onOpenChange={setPoOpen}
        suppliers={suppliers}
        jobs={jobs}
        materials={materials}
        onCreated={reload}
      />
    </>
  );
}
